/**
 * Where an error code is rendered: on the field it belongs to, or in the step's
 * top form-level alert.
 *
 * The field components carry the a11y wiring (a `role="alert"` message strip,
 * `aria-invalid`, `aria-describedby`) that announces on insertion and ties the
 * message to the input — but only a code that genuinely identifies ONE field
 * may be routed there. A generic or multi-field code stays form-level, because
 * pinning `aria-invalid` to a specific (possibly valid) input misidentifies the
 * error to a screen reader (WCAG 3.3.1).
 *
 * Every code lives in EXACTLY one home. That single-home rule is what keeps the
 * two channels from ever announcing the same error twice, and keeps at most one
 * `role="alert"` on screen for a given submit.
 *
 * Codes are the keys of `ERROR_MESSAGES` (lib/messages.ts). `interaction_not_found`
 * and `csrf` are terminal and rendered by the error card, never inline — they
 * fall through to the `'form'` default defensively. Anything unrecognised is
 * `'form'` too: an unknown code is never attributed to a field.
 */
export type ErrorHome = 'email' | 'code' | 'password' | 'birthdate' | 'form';

export function errorHome(code: string): ErrorHome {
  switch (code) {
    case 'invalid_email':
      return 'email';
    case 'invalid_code':
      return 'code';
    case 'invalid_password':
      return 'password';
    case 'under_minimum_age':
      // The date of birth genuinely IS the invalid field here — unlike the
      // generic `invalid_request`, which stays form-level.
      return 'birthdate';
    default:
      return 'form';
  }
}
